<template>
  <div
    class="container press-releases-page"
    :class="{ 'mobile-or-tablet': isMobileOrTablet }"
  >
    <!-- 新闻卡片 -->

    <div
      class="press-releases-card"
      v-for="(item, index) in displayNewsItems"
      :key="index"
    >
      <div
        class="news-card"
        @click="handleNewsClick(item.id)"
        style="cursor: pointer"
      >
        <div class="image-wrapper">
          <img
            :src="item.coverImage || item.imageUrl || item.imageCn"
            alt="新闻图片"
            class="image"
          />
        </div>
        <div class="news-content">
          <div class="pressReleases-source">{{ getCurrentLangValue(item, 'media') }}</div>
          <div class="pressReleases-title">{{ getCurrentLangValue(item, 'title') }}</div>
          <div class="pressReleases-description">
            {{ getCurrentLangValue(item, 'content') | stripHtml }}
          </div>
          <div class="bottom">
            <time class="time">{{ item.date || item.newsDate }}</time>
          </div>
        </div>
      </div>
      <div class="pressReleases-line"></div>
    </div>
    <div v-if="!isMobileOrTablet" class="pressReleases-date" @click="toggleSort">
      日期
      <!-- AI-Generated Begin -->
      <i :class="['el-icon-' + (isAscending ? 'top' : 'bottom')]"></i>
      <!-- AI-Generated End -->
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="handlePageChange"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      allNewsItems: [], // 从接口获取的数据
      pageSize: 6, // 每页显示的数量
      currentPage: 1, // 当前页码
      total: 0, // 总数量
      isMobileOrTablet: false, // 用于判断是否为平板或移动端
      // AI-Generated Begin
      isAscending: false, // 控制排序方向，默认为降序
      // AI-Generated End
      isDataLoaded: false, // 标记数据是否已加载
      isLoading: false, // 标记是否正在加载数据
    };
  },
  computed: {
    // 获取当前语言环境
    currentLanguage() {
      return this.$store.state.isEn; // 0 为中文，1 为英文
    },
    // 计算属性，用于获取当前页要显示的数据
    displayNewsItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.allNewsItems.slice(start, end);
    },
    newsItems() {
      // 为了兼容现有代码，但实际分页使用 displayNewsItems
      return this.displayNewsItems;
    },
  },
  watch: {
    // 监听语言变化，重新加载数据
    '$store.state.isEn'() {
      this.currentPage = 1; // 重置为第一页
      this.allNewsItems = []; // 清空现有数据
      this.isDataLoaded = false; // 重置数据加载标记
      this.isLoading = false; // 重置加载状态
      this.init(); // 重新初始化数据
    }
  },
  created() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    // 在创建组件时初始化数据
    this.init();
  },
  // 使用 activated 钩子，适用于 keep-alive 组件
  activated() {
    // 无论何种方式进入页面，都重置为第一页并加载数据
    this.currentPage = 1;
    this.init();
  },
  mounted() {
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  destroyed() {
    // 无需其他清理工作
  },
  filters: {
    stripHtml: function (value) {
      if (!value) return "";
      // 移除 HTML 标签
      let result = value.replace(/<[^>]*>?/gm, "");
      // 解码 HTML 实体
      const tempElement = document.createElement('textarea');
      tempElement.innerHTML = result;
      return tempElement.value;
    },
  },
  methods: {
    init() {
      // 如果正在加载数据，则直接返回，避免重复请求
      if (this.isLoading) {
        return;
      }
      
      // 设置加载状态
      this.isLoading = true;
      
      // 获取新闻列表数据
      this.$api.get(
        "/news/list",
        { 
          current: this.currentPage, 
          pageSize: this.pageSize,
          timeSort: this.isAscending ? 1 : 0 // 1 为升序，0 为降序
        },
        (res) => {
          // 请求完成后重置加载状态
          this.isLoading = false;
          
          const { data, code, total } = res;
          
          if(code === 0) {
            // 根据接口返回的数据结构进行适配
            const formattedData = data.map(item => ({
              id: item.id,
              title: {
                cn: item.cnTitle,
                en: item.enTitle
              },
              date: item.newsDate,
              media: {
                cn: item.mediaCn,
                en: item.mediaEn
              },
              author: {
                cn: item.newsReporterCn,
                en: item.newsReporterEn
              },
              originalUrl: item.newsOriginUrl,
              coverImage: item.imageCn,
              content: {
                cn: item.newsContentCn,
                en: item.newsContentEn
              },
              videoUrl: item.videoCn
            }));
            
            // 如果是第一页，直接替换数据
            if(this.currentPage === 1) {
              this.allNewsItems = formattedData;
            } else {
              // 如果不是第一页，追加数据
              this.allNewsItems = [...this.allNewsItems, ...formattedData];
            }
            
            this.total = total || data.length;
            
            // 标记数据已加载
            this.isDataLoaded = true;
          } else {
            console.error('获取新闻列表失败', res.msg);
            // 请求失败后也要重置加载状态
            this.isLoading = false;
          }
        }
      )
    },
    // 获取当前语言环境的值
    getCurrentLangValue(item, fieldName) {
      if (!item[fieldName]) return '';
      
      const langIndex = this.currentLanguage; // 0 为中文，1 为英文
      const langKey = langIndex === 1 ? 'en' : 'cn';
      
      if (typeof item[fieldName] === 'object' && item[fieldName] !== null) {
        return item[fieldName][langKey] || item[fieldName]['cn'] || '';
      } else {
        // 如果不是对象，直接返回原值
        return item[fieldName] || '';
      }
    },
    // 处理新闻点击事件
    handleNewsClick(id) {
      // 使用window.open在新窗口打开详情页
      const routeData = this.$router.resolve({
        path: "/visitorGuide/pressReleasesDetail",
        query: {
          columnId: 65,
          id: id,
        },
      });
      window.open(routeData.href, '_blank');
    },
    // 处理页码改变事件
    handlePageChange(page) {
      this.currentPage = page;
      this.isDataLoaded = false; // 重置数据加载标记
      this.isLoading = false; // 重置加载状态
      // 重新获取数据
      this.init();
    },
    // 检查屏幕尺寸
    checkScreenSize() {
      this.isMobileOrTablet = window.innerWidth <= 968;
    },
    toggleSort() {
      this.isAscending = !this.isAscending;
      // 排序切换时重置到第一页
      this.currentPage = 1;
      this.isDataLoaded = false; // 重置数据加载标记
      this.isLoading = false; // 重置加载状态
      this.init();
    }
  }
};
</script>

<style scoped>
.container.press-releases-page {
  position: relative;
  max-width: 1400px;
  margin: 120px auto 0;
  padding: 0 20px;
}
.press-releases-card {
  width: 85%;
}

.news-card {
  display: flex;
  margin-bottom: 20px;
}

.image-wrapper {
  width: 300px;
  height: 220px;
  flex: 0 0 auto; /* 添加此行，防止图片区域被挤压 */
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 为文字内容区域添加样式，确保它占用剩余空间 */
.news-content {
  position: relative;
  flex: 1;
  min-width: 0; /* 防止内容溢出 */
  padding: 0 20px 25px;
}

.bottom {
  position: absolute;
  bottom: 0;
  right: 0;
  margin-top: 13px;
  line-height: 12px;
  display: flex;
  justify-content: flex-end;
}
.time {
  font-family: SourceHanSansCN-Regular;
  font-size: 14px;
  color: #585c64;
  letter-spacing: 0;
  text-align: right;
  line-height: 26px;
  font-weight: 400;
}
.pressReleases-line {
  background: #d8d8d8;
  width: 100%;
  height: 1px;
  margin-bottom: 24px;
}
.pressReleases-source {
  background: #ca9d76;
  font-family: SourceHanSansCN-Regular;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: 0;
  text-align: center;
  font-weight: 400;
  padding: 2px 10px;
  display: inline-block;
  min-width: fit-content;
}
.pressReleases-title {
  font-family: SourceHanSansCN-Bold;
  font-size: 20px;
  color: #000000;
  letter-spacing: 0;
  text-align: left;
  font-weight: 700;
  margin-top: 12px;
}
.pressReleases-description {
  font-family: SourceHanSansCN-Regular;
  font-size: 14px;
  color: #585c64;
  letter-spacing: 0;
  text-align: left;
  line-height: 26px;
  font-weight: 400;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 12px;
}
.pressReleases-date {
  position: absolute;
  top: 5px;
  right: 0;
  background: #ffffff;
  border: 1px solid rgba(44, 44, 44, 1);
  border-radius: 2px;
  font-family: PingFangSC-Regular;
  font-size: 14px;
  color: #000000;
  letter-spacing: 0;
  text-align: center;
  font-weight: 400;
  padding: 5px 19px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.pressReleases-date i {
  margin-left: 5px;
}

/* 分页容器样式 */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 40px;
}

/* 自定义分页样式，使其与项目风格一致 */
.press-releases-page >>> .el-pagination .number.active,
.press-releases-page >>> .el-pagination .number.is-active {
  color: #ca9d76;
}

.press-releases-page >>> .el-pagination .number:hover,
.press-releases-page >>> .el-pagination .btn-prev:hover,
.press-releases-page >>> .el-pagination .btn-next:hover {
  color: #ca9d76;
}

/* 响应式设计 - 大屏幕 */
@media (max-width: 1441px) {
  .press-releases-page {
    margin-top: 100px;
    width: 92%;
    transform: scale(1);
  }

  .image-wrapper {
    width: 260px;
    height: 190px;
  }

  .pressReleases-title {
    font-size: 18px;
  }

  .pressReleases-description {
    font-size: 13px;
    margin-top: 10px;
  }

  .pressReleases-source {
    font-size: 13px;
  }

  .time {
    font-size: 13px;
  }
}

/* 响应式设计 - 桌面常规屏幕 */
@media (max-width: 1240px) {
  .press-releases-page {
    margin-top: 80px;
  }

  .image-wrapper {
    width: 240px;
    height: 170px;
  }

  .pressReleases-title {
    font-size: 17px;
  }

  .pressReleases-description {
    font-size: 12px;
    line-height: 22px;
    margin-top: 8px;
  }

  .news-content {
    padding: 0 15px;
  }

  .pressReleases-source {
    font-size: 12px;
  }

  .time {
    font-size: 12px;
  }
}

/* 响应式设计 - 小屏幕桌面 */
@media (max-width: 1040px) {
  .press-releases-page {
    margin-top: 60px;
  }

  .image-wrapper {
    width: 220px;
    height: 160px;
  }

  .pressReleases-title {
    font-size: 16px;
  }

  .pressReleases-description {
    font-size: 11px;
    line-height: 20px;
    margin-top: 8px;
  }

  .news-content {
    padding: 0 10px;
  }

  .pressReleases-source {
    font-size: 11px;
  }

  .time {
    font-size: 11px;
  }
}

/* 平板设备样式 */
@media (max-width: 960px) {
  .press-releases-page {
    margin-top: 60px;
    padding: 0 15px;
  }

  .news-card {
    flex-direction: row; /* 保持左右结构 */
  }

  .image-wrapper {
    width: 200px;
    height: 135px;
    flex: 0 0 auto;
  }

  .news-content {
    width: calc(100% - 200px);
    padding: 0 10px;
  }

  .press-releases-card {
    width: 100%;
  }

  .pressReleases-title {
    font-size: 14px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 18px;
    margin-top: 8px;
  }

  .pressReleases-description {
    display: none; /* 在手机上仍然隐藏描述文本 */
  }

  .pagination-container {
    margin-right: 0;
  }

  .pressReleases-source {
    margin: 0;
    font-size: 12px;
  }

  .time {
    font-size: 12px;
  }
}

/* 移动设备样式 */
@media (max-width: 768px) {
  .container.press-releases-page {
    margin-top: 90px;
    padding: 0;
  }

  .news-card {
    flex-direction: row; /* 保持左右结构 */
    margin-bottom: 10px;
  }

  .image-wrapper {
    width: 160px;
    height: auto;
    flex: 0 0 auto;
  }

  .news-content {
    position: relative;
    width: calc(100% - 160px);
    padding: 0 8px 35px;
  }

  .pressReleases-title {
    font-size: 13px;
    margin-top: 5px;
  }

  .pressReleases-description {
    display: none; /* 在手机上仍然隐藏描述文本 */
  }

  .pressReleases-source {
    font-size: 11px;
  }
  .bottom {
    position: absolute;
    bottom: 0;
    left: 8px;
    margin-top: 3px;
    display: flex;
    justify-content: flex-start;
  }
  .time {
    font-size: 10px;
  }
  .pressReleases-line {
    margin-bottom: 15px;
  }
}

/* 小型移动设备样式 */
@media (max-width: 420px) {
  .press-releases-page {
    margin-top: 50px;
    padding: 0 5px;
  }

  .news-card {
    flex-direction: row; /* 保持左右结构 */
  }

  .image-wrapper {
    width: 140px;
    height: auto;
    flex: 0 0 auto;
  }

  .news-content {
    width: calc(100% - 140px);
    padding: 0 5px 30px;
  }

  .pressReleases-title {
    font-size: 13px;
  }

  .pressReleases-description {
    display: none; /* 隐藏描述文本 */
  }

  .pressReleases-source {
    font-size: 10px;
  }

  .time {
    font-size: 10px;
  }

  .pressReleases-date {
    font-size: 12px;
    padding: 4px 10px;
  }
}
</style>